// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  NumberInput,
  Select,
  Textarea,
  Badge,
  SimpleGrid,
  Progress,
  ThemeIcon,
  Loader,
  Center,
  Tabs,
  Table,
} from '@mantine/core';
import {
  IconCalendar,
  IconPlus,
  IconBeach,
  IconMedicalCross,
  IconBriefcase,
  IconAdjustments,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useDisclosure } from '@mantine/hooks';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { StatCard } from '../../components/StatCard';
import {
  useLeaveBalances,
  useLeaveTransactions,
  useAdjustLeaveBalance,
  useTeamLeaveSummary,
  type LeaveBalance,
  type LeaveTransaction,
} from '../../hooks/useLeaveBalances';

const LEAVE_TYPE_ICONS: Record<string, React.ReactNode> = {
  PTO: <IconBeach size={16} />,
  VACATION: <IconBeach size={16} />,
  SICK: <IconMedicalCross size={16} />,
  PERSONAL: <IconBriefcase size={16} />,
};

const LEAVE_TYPE_COLORS: Record<string, string> = {
  PTO: 'blue',
  VACATION: 'cyan',
  SICK: 'red',
  PERSONAL: 'grape',
  BEREAVEMENT: 'gray',
  JURY_DUTY: 'indigo',
  MILITARY: 'green',
  COMP_TIME: 'orange',
};

function formatHours(hours: number): string {
  if (hours === 0) return '0h';
  const days = Math.floor(hours / 8);
  const remainingHours = hours % 8;
  if (days > 0 && remainingHours > 0) return `${days}d ${remainingHours}h`;
  if (days > 0) return `${days}d`;
  return `${hours}h`;
}

export default function LeaveBalancePage() {
  const [adjustOpened, { open: openAdjust, close: closeAdjust }] = useDisclosure(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [adjustForm, setAdjustForm] = useState({
    leaveType: '',
    hours: 0,
    reason: '',
    transactionType: 'ADJUSTMENT',
  });

  const { data: balancesRaw, loading: isLoading } = useLeaveBalances(selectedEmployee || undefined);
  const balances = balancesRaw as unknown as LeaveBalance[] | undefined;
  const { data: transactionsRaw } = useLeaveTransactions(
    selectedEmployee || '',
    selectedType || undefined,
  );
  const transactions = transactionsRaw as any;
  const { data: teamSummaryRaw } = useTeamLeaveSummary();
  const teamSummary = teamSummaryRaw as any;
  const adjustMutation = useAdjustLeaveBalance();

  const handleAdjust = () => {
    if (!adjustForm.leaveType || !adjustForm.hours || !adjustForm.reason.trim()) return;

    adjustMutation.mutate(
      {
        employeeId: selectedEmployee || '',
        leaveType: adjustForm.leaveType,
        hours: adjustForm.hours,
        reason: adjustForm.reason.trim(),
        transactionType: adjustForm.transactionType,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Balance Adjusted',
            message: `${formatHours(Math.abs(adjustForm.hours))} ${adjustForm.hours > 0 ? 'added to' : 'deducted from'} ${adjustForm.leaveType}`,
            color: 'green',
          });
          closeAdjust();
          setAdjustForm({ leaveType: '', hours: 0, reason: '', transactionType: 'ADJUSTMENT' });
        },
        onError: (err: any) => {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to adjust balance',
            color: 'red',
          });
        },
      },
    );
  };

  // Summary stats
  const totalAvailable = balances?.reduce((sum, b) => sum + b.available, 0) ?? 0;
  const totalUsed = balances?.reduce((sum, b) => sum + b.used, 0) ?? 0;
  const totalPending = balances?.reduce((sum, b) => sum + b.pending, 0) ?? 0;
  const totalAccrued = balances?.reduce((sum, b) => sum + b.accrued, 0) ?? 0;

  return (
    <Stack gap="md">
      <PageHeader
        title="Leave & Absence Management"
        subtitle="Track leave balances, accruals, and time-off usage"
        breadcrumbs={[
          { label: 'Requests', to: '/admin/workforce/leave/requests' },
          { label: 'Leave Balances' },
        ]}
        actions={
          <Button icon={<IconAdjustments size={16} />} onClick={openAdjust}>
            Adjust Balance
          </Button>
        }
      />

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 2, md: 4 }}>
        <StatCard
          title="Available"
          value={formatHours(totalAvailable)}
          icon={<IconCalendar size={20} />}
          color="green"
        />
        <StatCard
          title="Used"
          value={formatHours(totalUsed)}
          icon={<IconCalendar size={20} />}
          color="blue"
        />
        <StatCard
          title="Pending"
          value={formatHours(totalPending)}
          icon={<IconCalendar size={20} />}
          color="orange"
        />
        <StatCard
          title="Total Accrued"
          value={formatHours(totalAccrued)}
          icon={<IconCalendar size={20} />}
          color="gray"
        />
      </SimpleGrid>

      <Tabs defaultValue="balances">
        <Tabs.List>
          <Tabs.Tab value="balances">My Balances</Tabs.Tab>
          <Tabs.Tab value="transactions">Transaction History</Tabs.Tab>
          <Tabs.Tab value="team">Team Overview</Tabs.Tab>
        </Tabs.List>

        {/* Balances Tab */}
        <Tabs.Panel value="balances" pt="md">
          {isLoading ? (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          ) : !balances || balances.length === 0 ? (
            <EmptyState
              icon={<IconCalendar size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
              title="No Leave Balances"
              message="No leave balances configured. Contact your administrator."
            />
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
              {balances.map((balance) => {
                const percentUsed =
                  balance.accrued > 0
                    ? ((balance.used + balance.pending) / balance.accrued) * 100
                    : 0;
                const color = LEAVE_TYPE_COLORS[balance.leaveType] || 'blue';

                return (
                  <Paper key={balance.id} p="lg" withBorder radius="md">
                    <Group justify="space-between" mb="sm">
                      <Group gap="sm">
                        <ThemeIcon size="md" variant="light" color={color}>
                          {LEAVE_TYPE_ICONS[balance.leaveType] || <IconCalendar size={16} />}
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={600}>
                            {balance.leaveType.replace(/_/g, ' ')}
                          </Text>
                          {balance.policyName && (
                            <Text size="xs" c="dimmed">
                              {balance.policyName}
                            </Text>
                          )}
                        </div>
                      </Group>
                      <Badge size="lg" variant="light" color={color}>
                        {formatHours(balance.available)}
                      </Badge>
                    </Group>

                    <Progress.Root size="lg" mb="sm">
                      <Progress.Section
                        value={(balance.used / (balance.accrued || 1)) * 100}
                        color={color}
                      />
                      <Progress.Section
                        value={(balance.pending / (balance.accrued || 1)) * 100}
                        color="orange"
                      />
                    </Progress.Root>

                    <SimpleGrid cols={3} spacing="xs">
                      <div>
                        <Text size="xs" c="dimmed">
                          Accrued
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatHours(balance.accrued)}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          Used
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatHours(balance.used)}
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">
                          Pending
                        </Text>
                        <Text size="sm" fw={500} c="orange">
                          {formatHours(balance.pending)}
                        </Text>
                      </div>
                    </SimpleGrid>

                    {balance.carryover > 0 && (
                      <Text size="xs" c="dimmed" mt="xs">
                        Includes {formatHours(balance.carryover)} carryover
                      </Text>
                    )}

                    {balance.nextAccrualDate && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Next accrual: {new Date(balance.nextAccrualDate).toLocaleDateString()}
                        {balance.accrualRate
                          ? ` (+${formatHours(balance.accrualRate)})`
                          : ''}
                      </Text>
                    )}

                    {balance.maxAccrual && percentUsed < 100 && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Max accrual: {formatHours(balance.maxAccrual)}
                      </Text>
                    )}
                  </Paper>
                );
              })}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        {/* Transactions Tab */}
        <Tabs.Panel value="transactions" pt="md">
          <Paper withBorder>
            {!transactions || transactions.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No transactions found
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Leave Type</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Hours</Table.Th>
                    <Table.Th>Balance After</Table.Th>
                    <Table.Th>Description</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {transactions.map((txn: LeaveTransaction) => (
                    <Table.Tr key={txn.id}>
                      <Table.Td>{new Date(txn.date).toLocaleDateString()}</Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={LEAVE_TYPE_COLORS[txn.leaveType] || 'gray'}
                        >
                          {txn.leaveType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant="light"
                          color={
                            txn.transactionType === 'ACCRUAL' || txn.transactionType === 'CARRYOVER'
                              ? 'green'
                              : txn.transactionType === 'USAGE'
                                ? 'blue'
                                : txn.transactionType === 'FORFEIT'
                                  ? 'red'
                                  : 'gray'
                          }
                        >
                          {txn.transactionType}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          fw={500}
                          c={txn.hours > 0 ? 'green' : txn.hours < 0 ? 'red' : undefined}
                        >
                          {txn.hours > 0 ? '+' : ''}
                          {formatHours(txn.hours)}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatHours(txn.balanceAfter)}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={1}>
                          {txn.description}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>

        {/* Team Tab */}
        <Tabs.Panel value="team" pt="md">
          <Paper withBorder>
            {!teamSummary || teamSummary.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No team data available
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Employee</Table.Th>
                    <Table.Th>Department</Table.Th>
                    {teamSummary[0]?.balances.map((b: { leaveType: string; available: number; used: number; pending: number }) => (
                      <Table.Th key={b.leaveType}>{b.leaveType}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {teamSummary.map((member: { employeeId: string; employeeName: string; department: string; balances: { leaveType: string; available: number; used: number; pending: number }[] }) => (
                    <Table.Tr key={member.employeeId}>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {member.employeeName}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{member.department}</Text>
                      </Table.Td>
                      {member.balances.map((b: { leaveType: string; available: number; used: number; pending: number }) => (
                        <Table.Td key={b.leaveType}>
                          <Group gap={4}>
                            <Text size="sm" fw={500}>
                              {formatHours(b.available)}
                            </Text>
                            {b.pending > 0 && (
                              <Text size="xs" c="orange">
                                ({formatHours(b.pending)} pending)
                              </Text>
                            )}
                          </Group>
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Adjust Balance Modal */}
      <Modal opened={adjustOpened} onClose={closeAdjust} title="Adjust Leave Balance" size="md">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Manual adjustments create an audit trail entry. Use positive values to add hours and
            negative values to deduct.
          </Text>

          <Select
            label="Leave Type"
            placeholder="Select leave type"
            required
            value={adjustForm.leaveType}
            onChange={(v) => setAdjustForm((f) => ({ ...f, leaveType: v || '' }))}
            data={
              balances?.map((b) => ({
                value: b.leaveType,
                label: b.leaveType.replace(/_/g, ' '),
              })) || []
            }
          />

          <Select
            label="Adjustment Type"
            value={adjustForm.transactionType}
            onChange={(v) => setAdjustForm((f) => ({ ...f, transactionType: v || 'ADJUSTMENT' }))}
            data={[
              { value: 'ADJUSTMENT', label: 'Manual Adjustment' },
              { value: 'CARRYOVER', label: 'Carryover' },
              { value: 'FORFEIT', label: 'Forfeit' },
              { value: 'PAYOUT', label: 'Payout' },
            ]}
          />

          <NumberInput
            label="Hours"
            description="Positive to add, negative to deduct"
            required
            value={adjustForm.hours}
            onChange={(v) => setAdjustForm((f) => ({ ...f, hours: typeof v === 'number' ? v : 0 }))}
            decimalScale={2}
            step={0.5}
          />

          <Textarea
            label="Reason"
            placeholder="Required for audit trail..."
            required
            value={adjustForm.reason}
            onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.currentTarget.value }))}
            minRows={2}
          />

          <Paper p="sm" bg="blue.0" radius="sm">
            <Text size="xs" c="blue.7">
              Append-only: This adjustment creates an immutable audit record. It cannot be edited or
              deleted after creation.
            </Text>
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={closeAdjust}>
              Cancel
            </Button>
            <Button
              onClick={handleAdjust}
              loading={adjustMutation.isPending}
              disabled={!adjustForm.leaveType || !adjustForm.hours || !adjustForm.reason.trim()}
            >
              Apply Adjustment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
